import React, { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Formik, Field, Form } from "formik";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { isLoginAction } from "../../store/reducers/isOpenSlice";
import api from "../../api";
import { setAuth } from "../../utils/auth";
import { useTranslation } from "react-i18next";

// ─── Validators ──────────────────────────────────────────────────────────────
const isValidEmail = (value) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(value);
const isValidPhone = (value) => /^[0-9]{10,15}$/.test(value);

// ─── Views ───────────────────────────────────────────────────────────────────
const VIEWS = { LOGIN: "login", FORGOT: "forgot", RESET: "reset" };

const Login = ({ setIsOpenRegister }) => {
  const dispatch = useDispatch();
  const { isOpen } = useSelector((state) => state.isOpen);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [view, setView] = useState(VIEWS.LOGIN);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotStatus, setForgotStatus] = useState(null); // { success, message }
  const [resetStatus, setResetStatus] = useState(null);

  const handleClose = () => {
    dispatch(isLoginAction(false));
    setView(VIEWS.LOGIN);
    setForgotStatus(null);
    setResetStatus(null);
  };

  // ── Login ────────────────────────────────────────────────────────────────
  const LoginSchema = Yup.object().shape({
    password: Yup.string().required("Password is required!"),
    loginField: Yup.string()
      .required("Email or phone number is required!")
      .test("is-valid-email-or-phone", "Invalid email or phone number", (v) =>
        isValidEmail(v) || isValidPhone(v)
      ),
  });

  const handleLoginSubmit = async (data, { setStatus, setSubmitting }) => {
    setStatus("");
    const loginData = { password: data.password };
    if (isValidEmail(data.loginField)) loginData.email = data.loginField;
    else loginData.phone = data.loginField;

    try {
      const res = await api.post("customer/login", loginData);
      const payload = res?.data?.data;
      if (payload?.token && payload?.customer) {
        const { token, token_type = "Bearer", customer } = payload;
        if (data.rememberMe) {
          localStorage.setItem("auth", JSON.stringify({ token, token_type, customer }));
        } else {
          sessionStorage.setItem("auth", JSON.stringify({ token, token_type, customer }));
        }
        setAuth({ token, tokenType: token_type, customer });
        dispatch(isLoginAction(false));
        navigate("/user/dashboard");
        return;
      }
      setStatus("Login failed. Invalid server response.");
    } catch (err) {
      let errMsg = err?.response?.data?.message || "Invalid email, phone number or password.";
      if (err?.response?.data?.errors) {
        const details = Object.values(err.response.data.errors).flat().join(" ");
        if (details) errMsg = `${errMsg}: ${details}`;
      }
      setStatus(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Forgot Password ──────────────────────────────────────────────────────
  const ForgotSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email address").required("Email is required"),
  });

  const handleForgotSubmit = async (values, { setSubmitting }) => {
    setForgotStatus(null);
    try {
      await api.post("customer/forgot-password", { email: values.email });
      setForgotEmail(values.email);
      setForgotStatus({ success: true, message: "✅ Password reset link sent! Check your email inbox." });
    } catch (err) {
      const msg = err?.response?.data?.message || "Could not send reset email. Please check the email address.";
      setForgotStatus({ success: false, message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // ── Reset Password ───────────────────────────────────────────────────────
  const ResetSchema = Yup.object().shape({
    token: Yup.string().required("Reset token is required"),
    password: Yup.string().min(8, "Password must be at least 8 characters").required("New password is required"),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords do not match")
      .required("Please confirm your password"),
  });

  const handleResetSubmit = async (values, { setSubmitting }) => {
    setResetStatus(null);
    try {
      await api.post("customer/reset-password", {
        email: forgotEmail,
        token: values.token,
        password: values.password,
        password_confirmation: values.password_confirmation,
      });
      setResetStatus({ success: true, message: "✅ Password reset successful! You can now log in." });
      setTimeout(() => setView(VIEWS.LOGIN), 2000);
    } catch (err) {
      const msg = err?.response?.data?.message || "Invalid or expired reset token.";
      setResetStatus({ success: false, message: msg });
    } finally {
      setSubmitting(false);
    }
  };

  // ─── UI ──────────────────────────────────────────────────────────────────
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed inset-0 overflow-y-auto text-center z-30" onClose={handleClose}>
        <div className="min-h-screen px-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-60" />
          </Transition.Child>
          <span className="inline-block h-screen align-middle" aria-hidden="true">​</span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="inline-block w-full max-w-lg p-10 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="overflow-hidden bg-white mx-auto">

                {/* ── LOGIN VIEW ── */}
                {view === VIEWS.LOGIN && (
                  <>
                    <div className="text-center mb-6">
                      <h2 className="text-3xl font-bold text-black">{t("login.title")}</h2>
                      <p className="text-sm md:text-base text-gray-500 mt-2 mb-8 sm:mb-10">{t("login.subtitle")}</p>
                    </div>
                    <Formik
                      validationSchema={LoginSchema}
                      initialValues={{ password: "", loginField: "", rememberMe: false }}
                      onSubmit={handleLoginSubmit}
                    >
                      {({ errors, touched, isSubmitting, status }) => (
                        <Form className="flex flex-col justify-center">
                          <div className="grid grid-cols-1 gap-5">
                            <div className="grid">
                              <label htmlFor="loginField" className="block text-gray-500 font-medium text-sm leading-none mb-2">
                                {t("login.emailOrPhone")}
                              </label>
                              <div className="relative">
                                <Field
                                  className="py-2 pl-10 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                                  id="loginField"
                                  name="loginField"
                                  placeholder={t("login.emailOrPhonePlaceholder")}
                                  type="text"
                                />
                              </div>
                              {errors.loginField && touched.loginField && (
                                <span className="text-red-400 text-sm mt-2">{errors.loginField}</span>
                              )}
                            </div>

                            <div className="grid">
                              <div className="flex items-center justify-between mb-2">
                                <label className="block text-gray-500 font-medium text-sm leading-none" htmlFor="password">
                                  {t("login.password")}
                                </label>
                                <button
                                  type="button"
                                  onClick={() => setView(VIEWS.FORGOT)}
                                  className="text-xs text-emerald-600 hover:text-emerald-700 hover:underline font-medium transition"
                                >
                                  Forgot Password?
                                </button>
                              </div>
                              <div className="relative">
                                <Field
                                  className="py-2 pl-10 pr-10 w-full appearance-none border text-sm opacity-75 text-input rounded-md placeholder-body min-h-12 transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                                  id="password"
                                  name="password"
                                  placeholder={t("login.passwordPlaceholder")}
                                  type={showPassword ? "text" : "password"}
                                />
                                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors">
                                  {showPassword ? (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                  ) : (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                  )}
                                </button>
                              </div>
                              {errors.password && touched.password && (
                                <span className="text-red-400 text-sm mt-2">{errors.password}</span>
                              )}
                            </div>

                            <div className="flex items-center mb-4">
                              <Field type="checkbox" id="rememberMe" name="rememberMe" className="mr-2" />
                              <label htmlFor="rememberMe" className="text-sm text-gray-600">{t("login.rememberMe")}</label>
                            </div>

                            <button type="submit" disabled={isSubmitting} className="w-full text-center py-3 rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-all focus:outline-none my-1">
                              {isSubmitting ? t("login.loggingIn") : t("login.submit")}
                            </button>
                            {status && <span className="text-red-500 text-sm text-center">{status}</span>}
                          </div>
                        </Form>
                      )}
                    </Formik>
                    <div className="text-center text-sm text-gray-900 mt-4">
                      <div className="text-gray-500 mt-2.5">
                        {t("login.noAccount")}
                        <button onClick={() => { setIsOpenRegister(true); dispatch(isLoginAction(false)); }} className="text-gray-800 hover:text-emerald-500 font-bold mx-2">
                          {t("login.register")}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {/* ── FORGOT PASSWORD VIEW ── */}
                {view === VIEWS.FORGOT && (
                  <>
                    <div className="text-center mb-6">
                      <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-7 h-7 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold text-black">Forgot Password?</h2>
                      <p className="text-sm text-gray-500 mt-2">Enter your email and we'll send you a reset link.</p>
                    </div>

                    {forgotStatus ? (
                      <div className={`p-4 rounded-lg text-sm text-center mb-4 ${forgotStatus.success ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                        {forgotStatus.message}
                      </div>
                    ) : null}

                    <Formik
                      initialValues={{ email: "" }}
                      validationSchema={ForgotSchema}
                      onSubmit={handleForgotSubmit}
                    >
                      {({ errors, touched, isSubmitting }) => (
                        <Form className="flex flex-col gap-4">
                          <div>
                            <label htmlFor="forgot-email" className="block text-gray-500 font-medium text-sm mb-2">Email Address</label>
                            <Field
                              id="forgot-email"
                              name="email"
                              type="email"
                              placeholder="Enter your registered email"
                              className="py-2 px-4 w-full appearance-none border text-sm rounded-md transition duration-200 focus:ring-0 ease-in-out bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                            />
                            {errors.email && touched.email && <span className="text-red-400 text-xs mt-1">{errors.email}</span>}
                          </div>
                          <button type="submit" disabled={isSubmitting} className="w-full py-3 rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-all font-medium">
                            {isSubmitting ? "Sending..." : "Send Reset Link"}
                          </button>
                          {forgotStatus?.success && (
                            <button type="button" onClick={() => setView(VIEWS.RESET)} className="w-full py-2 text-sm text-emerald-600 hover:underline">
                              I have the reset code →
                            </button>
                          )}
                        </Form>
                      )}
                    </Formik>

                    <div className="text-center mt-5">
                      <button onClick={() => { setView(VIEWS.LOGIN); setForgotStatus(null); }} className="text-sm text-gray-500 hover:text-emerald-600 transition">
                        ← Back to Login
                      </button>
                    </div>
                  </>
                )}

                {/* ── RESET PASSWORD VIEW ── */}
                {view === VIEWS.RESET && (
                  <>
                    <div className="text-center mb-6">
                      <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-7 h-7 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <h2 className="text-2xl font-bold text-black">Reset Password</h2>
                      <p className="text-sm text-gray-500 mt-2">
                        Enter the token from your email and your new password.
                      </p>
                    </div>

                    {resetStatus && (
                      <div className={`p-4 rounded-lg text-sm text-center mb-4 ${resetStatus.success ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
                        {resetStatus.message}
                      </div>
                    )}

                    <Formik
                      initialValues={{ token: "", password: "", password_confirmation: "" }}
                      validationSchema={ResetSchema}
                      onSubmit={handleResetSubmit}
                    >
                      {({ errors, touched, isSubmitting }) => (
                        <Form className="flex flex-col gap-4">
                          <div>
                            <label htmlFor="reset-token" className="block text-gray-500 font-medium text-sm mb-2">Reset Token</label>
                            <Field
                              id="reset-token"
                              name="token"
                              type="text"
                              placeholder="Paste token from email"
                              className="py-2 px-4 w-full appearance-none border text-sm rounded-md transition duration-200 focus:ring-0 bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12 font-mono"
                            />
                            {errors.token && touched.token && <span className="text-red-400 text-xs mt-1">{errors.token}</span>}
                          </div>
                          <div>
                            <label htmlFor="new-password" className="block text-gray-500 font-medium text-sm mb-2">New Password</label>
                            <div className="relative">
                              <Field
                                id="new-password"
                                name="password"
                                type={showNewPassword ? "text" : "password"}
                                placeholder="Min. 8 characters"
                                className="py-2 px-4 pr-10 w-full appearance-none border text-sm rounded-md transition duration-200 focus:ring-0 bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                              />
                              <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400">
                                {showNewPassword ? (
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                                ) : (
                                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                )}
                              </button>
                            </div>
                            {errors.password && touched.password && <span className="text-red-400 text-xs mt-1">{errors.password}</span>}
                          </div>
                          <div>
                            <label htmlFor="confirm-password" className="block text-gray-500 font-medium text-sm mb-2">Confirm Password</label>
                            <Field
                              id="confirm-password"
                              name="password_confirmation"
                              type="password"
                              placeholder="Repeat new password"
                              className="py-2 px-4 w-full appearance-none border text-sm rounded-md transition duration-200 focus:ring-0 bg-white border-gray-200 focus:outline-none focus:border-emerald-500 h-11 md:h-12"
                            />
                            {errors.password_confirmation && touched.password_confirmation && (
                              <span className="text-red-400 text-xs mt-1">{errors.password_confirmation}</span>
                            )}
                          </div>
                          <button type="submit" disabled={isSubmitting} className="w-full py-3 rounded bg-emerald-500 text-white hover:bg-emerald-600 transition-all font-medium">
                            {isSubmitting ? "Resetting..." : "Reset Password"}
                          </button>
                        </Form>
                      )}
                    </Formik>

                    <div className="text-center mt-5">
                      <button onClick={() => { setView(VIEWS.FORGOT); setResetStatus(null); }} className="text-sm text-gray-500 hover:text-emerald-600 transition">
                        ← Back
                      </button>
                    </div>
                  </>
                )}

              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default Login;
